import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { BackButton } from '@/components/ui/back-button';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { useInfiniteScrollList } from '@/hooks/useInfiniteScrollList';
import { organizationApi, permissionsApi } from '@/lib/pillarApi';
import { useAuthStore } from '@/store/auth';
import { useDataInvalidationStore } from '@/store/dataInvalidation';
import type {
    GetOrganizationResponse,
    PermissionsBundlePublic,
    PolicyPermissionBundleRoleAssignmentListItem,
    PolicyPermissionBundleRoleAssignmentsSearchPageResponse,
} from '@anchorbase/pillar-sdk';
import { useQuery } from '@tanstack/react-query';
import { formatOrganizationDisplayName } from './organizationDisplayName';

const PolicyRoleAssignmentCursorSchema: z.ZodType<string> = z.string().trim().min(1);

export function PolicyPermissionBundleRoleAssignmentsPage() {
    const navigate = useNavigate();
    const params = useParams();
    const organizationUuid: string | null = useMemo(() => {
        const candidate = params.id;
        const parseResult = z.string().uuid().safeParse(candidate);
        return parseResult.success ? parseResult.data : null;
    }, [params.id]);
    const permissionsBundleUuid: string | null = useMemo(() => {
        const candidate = params.policyUuid;
        const parseResult = z.string().uuid().safeParse(candidate);
        return parseResult.success ? parseResult.data : null;
    }, [params.policyUuid]);

    const activeRoleAssignmentId: number | null = useAuthStore(
        (state) => state.activeRoleAssignmentId
    );
    const policyGrantsVersion: number = useDataInvalidationStore(
        (state) => state.policyGrantsVersion
    );
    const bumpPolicyGrantsVersion = useDataInvalidationStore(
        (state) => state.bumpPolicyGrantsVersion
    );

    const [refreshToken, setRefreshToken] = useState<number>(0);
    const [pendingGrantStateByRoleAssignmentUuid, setPendingGrantStateByRoleAssignmentUuid] =
        useState<Record<string, boolean>>({});
    const [persistedGrantStateByRoleAssignmentUuid, setPersistedGrantStateByRoleAssignmentUuid] =
        useState<Record<string, boolean>>({});

    const { data: organization, isLoading: isOrganizationLoading } =
        useQuery<GetOrganizationResponse>({
            queryKey: ['organization', organizationUuid],
            queryFn: async () => {
                if (organizationUuid === null) {
                    throw new Error('Invalid organization UUID');
                }
                return await organizationApi.getOrganization(organizationUuid);
            },
            enabled: organizationUuid !== null,
            staleTime: 30000,
        });
    const {
        data: permissionsBundle,
        isLoading: isPermissionsBundleLoading,
        error: permissionsBundleError,
    } = useQuery<PermissionsBundlePublic>({
        queryKey: ['policyBundle', permissionsBundleUuid],
        queryFn: async () => {
            if (permissionsBundleUuid === null) {
                throw new Error('Invalid permissions bundle UUID');
            }
            return await permissionsApi.getPolicyPermissionBundle(
                permissionsBundleUuid
            );
        },
        enabled: permissionsBundleUuid !== null,
        staleTime: 30000,
    });

    const list = useInfiniteScrollList<
        PolicyPermissionBundleRoleAssignmentListItem,
        string,
        readonly unknown[]
    >({
        queryKeyPrefix: [
            'policyBundleRoleAssignments',
            `org=${organizationUuid ?? 'none'}`,
            `bundle=${permissionsBundleUuid ?? 'none'}`,
            `role=${activeRoleAssignmentId ?? 'none'}`,
            `v=${policyGrantsVersion}`,
            `refresh=${refreshToken}`,
        ] as const,
        pageSize: 50,
        enabled: organizationUuid !== null && permissionsBundleUuid !== null,
        fetchPage: async (params) => {
            if (organizationUuid === null || permissionsBundleUuid === null) {
                throw new Error('Missing route parameters');
            }
            const response: PolicyPermissionBundleRoleAssignmentsSearchPageResponse =
                await permissionsApi.searchRoleAssignmentsForPolicyPermissionBundle(
                    organizationUuid,
                    permissionsBundleUuid,
                    {
                        ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                        limit: params.limit,
                        cursor: params.cursor,
                    }
                );
            return {
                items: response.role_assignments,
                pagination: response.pagination,
            };
        },
        parseCursorParam: (pageParam: unknown) => {
            if (pageParam === null || pageParam === undefined) {
                return undefined;
            }
            const parseResult = PolicyRoleAssignmentCursorSchema.safeParse(pageParam);
            if (!parseResult.success) {
                throw new Error('Expected opaque cursor');
            }
            return parseResult.data;
        },
    });

    const currentRoleAssignments: ReadonlyArray<PolicyPermissionBundleRoleAssignmentListItem> =
        list.items;

    useEffect(() => {
        setPersistedGrantStateByRoleAssignmentUuid((current) => {
            let hasChanges = false;
            const nextState: Record<string, boolean> = { ...current };
            for (const roleAssignment of currentRoleAssignments) {
                const roleAssignmentUuid = roleAssignment.role_assignment_uuid;
                const hasPendingOverride =
                    pendingGrantStateByRoleAssignmentUuid[roleAssignmentUuid] !== undefined;
                if (hasPendingOverride) {
                    continue;
                }
                if (nextState[roleAssignmentUuid] === roleAssignment.is_granted) {
                    continue;
                }
                nextState[roleAssignmentUuid] = roleAssignment.is_granted;
                hasChanges = true;
            }
            return hasChanges ? nextState : current;
        });
    }, [currentRoleAssignments, pendingGrantStateByRoleAssignmentUuid]);

    const currentGrantStateByRoleAssignmentUuid: Record<string, boolean> = useMemo(() => {
        const nextState: Record<string, boolean> = {};
        for (const roleAssignment of currentRoleAssignments) {
            const pendingState =
                pendingGrantStateByRoleAssignmentUuid[roleAssignment.role_assignment_uuid];
            nextState[roleAssignment.role_assignment_uuid] =
                pendingState ?? roleAssignment.is_granted;
        }
        return nextState;
    }, [currentRoleAssignments, pendingGrantStateByRoleAssignmentUuid]);

    const dirtyRoleAssignmentUuids: string[] = useMemo(() => {
        const nextDirtyRoleAssignmentUuids: string[] = [];
        for (const roleAssignmentUuid in pendingGrantStateByRoleAssignmentUuid) {
            const nextValue = pendingGrantStateByRoleAssignmentUuid[roleAssignmentUuid];
            if (nextValue === undefined) {
                continue;
            }
            const persistedValue =
                persistedGrantStateByRoleAssignmentUuid[roleAssignmentUuid];
            if (persistedValue === undefined || persistedValue === nextValue) {
                continue;
            }
            nextDirtyRoleAssignmentUuids.push(roleAssignmentUuid);
        }
        return nextDirtyRoleAssignmentUuids;
    }, [pendingGrantStateByRoleAssignmentUuid, persistedGrantStateByRoleAssignmentUuid]);

    const saveMutation = useMutation({
        mutationFn: async () => {
            if (permissionsBundleUuid === null) {
                throw new Error('Missing permissions bundle uuid');
            }
            for (const roleAssignmentUuid of dirtyRoleAssignmentUuids) {
                const nextValue = pendingGrantStateByRoleAssignmentUuid[roleAssignmentUuid];
                if (nextValue === undefined) {
                    continue;
                }
                if (nextValue) {
                    await permissionsApi.grantPolicyPermissionBundleToRoleAssignment(
                        roleAssignmentUuid,
                        {
                            permissions_bundle_uuid: permissionsBundleUuid,
                        }
                    );
                    continue;
                }
                await permissionsApi.revokePolicyPermissionBundleFromRoleAssignment(
                    roleAssignmentUuid,
                    permissionsBundleUuid
                );
            }
        },
        onSuccess: () => {
            bumpPolicyGrantsVersion();
            setPendingGrantStateByRoleAssignmentUuid({});
            setPersistedGrantStateByRoleAssignmentUuid({});
            setRefreshToken((value) => value + 1);
        },
    });

    const errorMessage: string | null = useMemo(() => {
        if (list.error instanceof Error) {
            return list.error.message;
        }
        if (permissionsBundleError instanceof Error) {
            return permissionsBundleError.message;
        }
        if (saveMutation.error instanceof Error) {
            return saveMutation.error.message;
        }
        return null;
    }, [list.error, permissionsBundleError, saveMutation.error]);

    if (
        organizationUuid === null ||
        permissionsBundleUuid === null
    ) {
        return <div className="text-destructive">Invalid route parameters.</div>;
    }
    const organizationDisplayName: string =
        organization !== undefined
            ? formatOrganizationDisplayName(organization)
            : 'Organization';
    const policyName: string = permissionsBundle?.name ?? 'Policy';
    const organizationDetailsHref: string =
        organization?.is_merchant === true
            ? `/organizations/${organizationUuid}?view=merchant`
            : organization?.is_iso === true
              ? `/organizations/${organizationUuid}?view=iso`
              : organization?.is_customer === true
                ? `/organizations/${organizationUuid}?view=customer`
                : `/organizations/${organizationUuid}`;

    return (
        <div
            id="policy-permission-bundle-role-assignments-page"
            data-organization-uuid={organizationUuid}
            data-permissions-bundle-uuid={permissionsBundleUuid}
            className="space-y-6"
        >
            <BackButton href={organizationDetailsHref} tooltip="Back to Organization Details" />

            <div>
                <h1 className="text-3xl font-bold">{policyName}</h1>
                <p className="text-sm text-muted-foreground mt-2">
                    Manage this policy bundle across role assignments in{' '}
                    {organizationDisplayName}.
                </p>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Users</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="mb-4 flex items-center justify-between gap-4">
                        <Input
                            id="policy-permission-bundle-role-assignments-search"
                            placeholder="Search role assignments..."
                            value={list.queryText}
                            onChange={(event) => {
                                list.setQueryText(event.target.value);
                            }}
                            className="w-[320px]"
                        />
                        <div className="flex items-center gap-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setPendingGrantStateByRoleAssignmentUuid((current) => {
                                        const nextState = { ...current };
                                        for (const roleAssignment of currentRoleAssignments) {
                                            nextState[roleAssignment.role_assignment_uuid] = true;
                                        }
                                        return nextState;
                                    });
                                }}
                                disabled={currentRoleAssignments.length === 0}
                            >
                                Select all
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setPendingGrantStateByRoleAssignmentUuid((current) => {
                                        const nextState = { ...current };
                                        for (const roleAssignment of currentRoleAssignments) {
                                            nextState[roleAssignment.role_assignment_uuid] = false;
                                        }
                                        return nextState;
                                    });
                                }}
                                disabled={currentRoleAssignments.length === 0}
                            >
                                Deselect all
                            </Button>
                            <Button
                                id="policy-permission-bundle-role-assignments-save"
                                type="button"
                                onClick={() => saveMutation.mutate()}
                                disabled={
                                    dirtyRoleAssignmentUuids.length === 0 ||
                                    saveMutation.isPending
                                }
                            >
                                {saveMutation.isPending ? 'Saving...' : 'Save'}
                            </Button>
                        </div>
                    </div>

                    {errorMessage !== null && (
                        <Alert
                            id="policy-permission-bundle-role-assignments-error"
                            variant="destructive"
                            className="mb-4"
                        >
                            <AlertDescription>{errorMessage}</AlertDescription>
                        </Alert>
                    )}

                    {isOrganizationLoading || isPermissionsBundleLoading || list.isLoading ? (
                        <p className="text-sm text-muted-foreground">Loading role assignments...</p>
                    ) : currentRoleAssignments.length === 0 ? (
                        <p className="text-sm text-muted-foreground">
                            No role assignments found for this organization.
                        </p>
                    ) : (
                        <>
                            <div className="space-y-2">
                                {currentRoleAssignments.map((roleAssignment) => {
                                    const checked =
                                        currentGrantStateByRoleAssignmentUuid[
                                            roleAssignment.role_assignment_uuid
                                        ] ?? roleAssignment.is_granted;
                                    return (
                                        <div
                                            key={roleAssignment.role_assignment_uuid}
                                            id={`policy-role-assignment-${roleAssignment.role_assignment_uuid}`}
                                            data-role-assignment-uuid={
                                                roleAssignment.role_assignment_uuid
                                            }
                                            data-user-uuid={roleAssignment.user_uuid}
                                            data-primary-email={roleAssignment.primary_email}
                                            data-role={roleAssignment.role}
                                            data-is-granted={String(checked)}
                                            className="flex items-center justify-between rounded-lg border p-4"
                                        >
                                            <div className="flex items-center gap-4">
                                                <Checkbox
                                                    checked={checked}
                                                    onCheckedChange={(nextChecked) => {
                                                        setPendingGrantStateByRoleAssignmentUuid(
                                                            (current) => ({
                                                                ...current,
                                                                [roleAssignment.role_assignment_uuid]:
                                                                    nextChecked === true,
                                                            })
                                                        );
                                                    }}
                                                    aria-label={`Grant policy to ${roleAssignment.user_display_name} (${roleAssignment.role})`}
                                                />
                                                <button
                                                    type="button"
                                                    className="text-left"
                                                    onClick={() => {
                                                        Promise.resolve(
                                                            navigate(
                                                                `/users/${roleAssignment.user_uuid}`
                                                            )
                                                        ).catch(() => {});
                                                    }}
                                                >
                                                    <div className="font-medium">
                                                        {roleAssignment.user_display_name}
                                                    </div>
                                                    <div className="text-sm text-muted-foreground">
                                                        {roleAssignment.primary_email}
                                                    </div>
                                                </button>
                                            </div>
                                            <Badge variant="secondary">
                                                {roleAssignment.role}
                                            </Badge>
                                        </div>
                                    );
                                })}
                            </div>
                            <div ref={list.loadMoreSentinelRef} className="h-8" />
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}
